// Client helpers for the share-links UI.
//
// Three actions mirroring the Share menu in the SPA:
//   - buildDriveLink(file): derived URL (no API call), constructs
//     <this page's origin>/share?ownerId=…&fileId=… via @papan/share.
//   - mintAppShare(file): POST /api/share-links/app
//   - mintQuickViewShare(file): POST /api/share-links/quick-view
//
// Each helper returns the share URL + the minted row.

import { request, fetchRaw } from '@papan/api-client'
import { driveLink, LOCAL_DEV_PORT, type ShareKind } from '@papan/share'
import { isMiniappFolder, isHtmlFile, shareKindForFile as sharedShareKindForFile } from '@papan/types'

/** One breadcrumb / ancestor node — the shape every breadcrumb source uses. */
export interface CrumbLike {
  id: string
  name: string
}

/**
 * Owner-side origin for derived links.
 *
 * A drive link targets the SPA's own `/share` route, so the correct origin is
 * simply wherever this page was loaded from. This replaced a hostname→env
 * label derivation that only recognised `*.papan.ai` and silently fell back to
 * `'local'` — i.e. every drive link minted on any other host (a branded
 * deployment, a `*.pages.dev` PR review env) pointed at `http://localhost:7474`.
 * Reading `location.origin` is both simpler and correct on every host.
 */
function webOrigin(): string {
  return typeof window === 'undefined' ? `http://localhost:${LOCAL_DEV_PORT}` : window.location.origin
}

/**
 * Decision 10 (+0083 amendment): per-recipient grant.
 *
 *   - `permission`:
 *     - `'view'` / `'edit'` grant access
 *     - `'block'` explicitly denies this recipient even when the
 *       `"*"` wildcard grants. Useful for "anyone EXCEPT this person".
 *   - `liveMode`: per-recipient — snapshot (false) vs live (true).
 *     The Edit permission is implicitly live (no coherent merge from a
 *     snapshot back to the owner's document); the resolver honors that
 *     so the stored boolean is essentially informational for edit rows.
 */
export type RecipientPermission = 'view' | 'edit' | 'block'
export interface RecipientGrant {
  permission: RecipientPermission
  liveMode: boolean
}
export type RecipientGrants = Record<string, RecipientGrant>

export interface ShareLinkRow {
  id: string
  token: string
  kind: ShareKind
  rootFileId: string
  /** (0084) The App root folder this share resolves files relative to.
   *  Only meaningful for kind='app' on a loose HTML root; null for
   *  kind='quick_view' and .miniapp folder roots. Mutable post-mint
   *  via PATCH /:id (active sessions are invalidated via config_version). */
  appRootFileId: string | null
  /** Sub-file reshare scope (0126/0127): the SOURCE sub-file this link is narrowed to;
   *  null for a normal / whole-mount link. Per-sub-file defaults key their app/source
   *  cards on it. */
  subSourceFileId: string | null
  workspaceId: string | null
  tenantId: string
  /** User id who created the link. Always present; resolve to a full
   *  user record via the `creator` field below. */
  createdBy: string
  /** (0084) Org-tenant convenience: creator's display name + email
   *  joined into the list response so the dialog can show "Created by …".
   *  null for personal-tenant lists (single user, attribution redundant)
   *  or when the creator user row no longer exists. */
  creator: { id: string; name: string | null; email: string } | null
  /**
   * Decision 10 (+0083): per-recipient permission map. Keys are
   * lowercased emails OR the `"*"` wildcard sentinel for "Anyone with
   * the link." Values carry both permission and per-recipient liveMode.
   */
  recipientGrants: RecipientGrants
  /** (0108) Per-permission Add-to-Drive approval bypass. When true, any
   *  recipient resolving to that permission — named or via the "*" wildcard —
   *  auto-establishes their mount (no owner confirm). Both default false =
   *  require approval. */
  autoApproveView: boolean
  autoApproveEdit: boolean
  /** (0119) Hard off-switch: when true, recipients can't Add-to-Drive at all
   *  (share-view button disabled, mount endpoint rejects). Default false. */
  forbidAddToDrive: boolean
  expiresAt: string | null
  viewCount: number
  /** The folder's auto-created default native link (migration 0112): pinned,
   *  immutable, non-revocable. Owner-created links are false. */
  isDefault: boolean
  isRevoked: boolean
  /** Decision 9 / Q6: cause of revocation. NULL = not revoked. */
  revokeReason: string | null
  createdAt: string
  lastViewedAt: string | null
  tokenShape: 'path' | 'subdomain' | null
  /** 0249: opens only for its creator and the people listed (or anyone, with the wildcard) — never
   *  through a visitor's own access. Every link minted since 0249; older links are false. */
  strictAudience?: boolean
}

export interface MintResult {
  shareLink: ShareLinkRow
  url: string
}

export interface MintArgs {
  fileId: string
  /** Optional. Server picks any workspace owned by the tenant if not provided. */
  workspaceId?: string
  /** ISO-8601 expiry. Omit/empty/null for no expiry. */
  expiresAt?: string | null
  /**
   * Decision 10 (+0083): per-recipient grants. Keys are emails
   * (lowercased by server) OR `"*"` for "Anyone with the link." Each
   * value carries permission + liveMode (the per-link liveMode column
   * was removed in 0083). Empty / omitted = link minted with no
   * recipients (dialog disables Create button until populated; staging
   * workflow via PATCH supported).
   */
  recipientGrants?: RecipientGrants
  /**
   * Phase 1 App root selection. Only meaningful for kind='app' + loose
   * HTML files. Must be an ancestor folder of the file. Omit to default
   * to the file's parent folder. Ignored for .miniapp folders + quick-
   * view shares (server stores NULL).
   */
  appRootFileId?: string | null
  /** (0108/0119) Add-to-Drive controls baked at mint (quick-view only —
   *  app shares aren't mountable). Omitted = defaults: auto-approve off,
   *  Add-to-Drive allowed. */
  autoApproveView?: boolean
  autoApproveEdit?: boolean
  forbidAddToDrive?: boolean
  /**
   * Sub-file reshare scope (2026-07-29 plan — RESHARE_SUBFILE, Phase 2/6).
   * Only meaningful when `fileId` is a `.shared`/`.chain` mount POINTER in the
   * caller's tenant: this SOURCE-tenant file id narrows the reshare link to a
   * single file/subfolder INSIDE the mount. The server validates containment
   * (`validateSubSourceScope`) and every access surface narrows to it. Omit for
   * a whole-mount reshare or any normal (non-mount) share. NOT accepted by the
   * auto-default link (`POST /default` returns `default_no_sub_scope`) — a
   * sub-file reshare is always an explicit `mintShareLink` call. */
  subSourceFileId?: string
}

/**
 * Build a Share to Drive URL. No API call — purely derived from the
 * file's owner-tenant + id. Access is gated at navigation time by the
 * recipient's tenant permissions.
 */
export function buildDriveLink(args: {
  ownerId: string
  fileId: string
  tenantId?: string
}): string {
  return driveLink({ webOrigin: webOrigin(), ...args })
}

/**
 * Mint a Share as an App link. Server validates eligibility (HTML or
 * .miniapp/ only) and returns 400 with `error: 'not_app_eligible'`
 * otherwise.
 */
export async function mintAppShare(args: MintArgs): Promise<MintResult> {
  return request<MintResult>('/api/share-links/app', {
    method: 'POST',
    body: JSON.stringify(args),
  })
}

/**
 * Mint a Share as public link (`kind='quick_view'`). All content types
 * are eligible — HTML and .miniapp folders can mint both an App link
 * AND a public link (different experiences from the same source).
 */
export async function mintQuickViewShare(args: MintArgs): Promise<MintResult> {
  return request<MintResult>('/api/share-links/quick-view', {
    method: 'POST',
    body: JSON.stringify(args),
  })
}

/**
 * Dispatch helper for the unified Share dialog (Decision 5). The dialog
 * picks the mode via a toggle inside it and calls this one entry point;
 * dispatch keeps mintAppShare / mintQuickViewShare around for callers
 * that already know the kind statically.
 */
export async function mintShareLink(
  args: MintArgs & { kind: ShareKind },
): Promise<MintResult> {
  const { kind, ...rest } = args
  return kind === 'app' ? mintAppShare(rest) : mintQuickViewShare(rest)
}

/** Heuristic for which "Share as …" action applies to a file. */
// The rule itself now lives in @papan/types beside isMiniappFolder — three
// callers need it (this dialog, the API route, the agent's share_link tool)
// and none can reach the others. Re-exported here so existing imports and the
// ShareKind return type are unchanged.
export const shareKindForFile: (args: { name: string; isFolder: boolean }) => ShareKind = sharedShareKindForFile

/**
 * Whether the unified Share dialog should offer the "App" mode for this
 * file. Public link is always eligible; App is the gated one.
 */
export function isAppEligibleForFile(args: { name: string; isFolder: boolean }): boolean {
  return shareKindForFile(args) === 'app'
}

/**
 * The `.miniapp` folders on a file's ancestor chain, in the SAME order as the
 * breadcrumb array (top-level → nearest). `crumbs` is any breadcrumb list — the
 * current folder's path (desktop/mobile) or a file's own breadcrumbs (editor).
 * A file element in the array never matches `isMiniappFolder`, so passing a
 * file-id breadcrumb list (which ends with the file itself) is safe.
 *
 * Multiple results ⇒ the file sits inside NESTED mini-apps; the caller shows a
 * picker. One result ⇒ share it directly. Zero ⇒ not in a mini-app.
 */
export function miniappAncestors(crumbs: readonly CrumbLike[]): CrumbLike[] {
  return crumbs.filter((c) => isMiniappFolder(c.name))
}

/**
 * Whether to offer "Share MiniApp" for a row: an HTML FILE (never a folder)
 * that has at least one `.miniapp` ancestor. The `!isFolder` guard means a
 * folder literally named `x.html` — or the `.miniapp` folder itself — never
 * qualifies.
 */
export function canShareMiniApp(
  file: { name: string; isFolder: boolean },
  crumbs: readonly CrumbLike[],
): boolean {
  return !file.isFolder && isHtmlFile(file.name) && miniappAncestors(crumbs).length > 0
}

/**
 * Whether the unified Share dialog should offer the Live updates pick.
 *
 *   Files only — folders intentionally do not get the toggle (the
 *   semantic of "all files inside default to live" is too indirect for
 *   the owner to intuit, see Decision 2 follow-up).
 *
 *   For files, eligibility = the content type has a collaborative
 *   surface (Y.Doc / Collabora). Plain binary previews (image, pdf,
 *   archive, video) have no realtime channel, so the toggle would do
 *   nothing. We whitelist by extension.
 *
 * Keep this in sync with the collab plugins in features/collab/server.
 */
export function isLiveModeEligibleForFile(args: { name: string; isFolder: boolean }): boolean {
  if (args.isFolder) return false
  const ext = args.name.toLowerCase().split('.').pop() ?? ''
  return LIVE_ELIGIBLE_EXTENSIONS.has(ext)
}

const LIVE_ELIGIBLE_EXTENSIONS = new Set([
  // Text / code with Y.Doc editor surface
  'md', 'txt', 'html', 'htm', 'js', 'jsx', 'ts', 'tsx',
  'py', 'json', 'css', 'scss', 'yaml', 'yml', 'xml', 'csv',
  'sql', 'sh', 'rb', 'go', 'rs', 'java', 'c', 'cpp', 'h',
  // Collabora office documents
  'odt', 'ods', 'odp', 'docx', 'xlsx', 'pptx',
])

// ─── Management API (per-file dialog) ────────────────────────────────

export interface ListShareLinksArgs {
  fileId?: string
  kind?: ShareKind
  includeRevoked?: boolean
}

/** What an APP link serves besides its entry file (#888) — see `appRootScopes` server-side. */
export interface AppRootScope {
  id: string
  name: string
  /** Direct children other than the entry file, `.`/`_` names left out. null = not counted. */
  otherItems: number | null
  /** Set when the folder is one of the drive's own (#889) — the link serves nothing. */
  blocked: string | null
}

export interface ShareLinkWithUrl extends ShareLinkRow {
  /** App links with an app folder: what the link serves (#888). Absent on other kinds. */
  appRoot?: AppRootScope | null
  /** May the caller move this app link's folder? Only its creator or the tenant owner
   *  (moving re-takes whose grants the link carries — #787 / 0227). */
  canRepoint?: boolean
  url: string
  /** Server-resolved per-row flag mirroring `guardEditRights`: caller is
   *  the creator AND `allow_sharing` is still true AND caller still has
   *  read access on `rootFileId`. The dialog uses this to swap the
   *  pencil for a chevron when the viewer can't actually edit. Only
   *  populated by the list endpoint; mutation endpoints (which only run
   *  when the caller passes the guard) leave it false by default. */
  canEdit?: boolean
  /** Token with the tenant slug prefix stripped (App-kind only). All
   *  rows in a single tenant's list share the same prefix, so showing
   *  it on every chip wastes pixels. The full `token` is still
   *  authoritative for URL building and copy actions; this is purely a
   *  display label. Falls back to `token` when stripping would be a
   *  no-op (quick-view, mismatched prefix, etc.). */
  displayToken?: string
}

/**
 * Decision 9 / Phase 23: share-link principals covering a specific file.
 * Each row is a link whose grant resolver returned non-none for this file
 * — direct grants OR cascaded folder grants. Used by WhoHasAccess to
 * display share-links alongside user/team/role/org principals.
 */
export interface ShareLinkPrincipal {
  id: string
  token: string
  kind: ShareKind
  /** Lattice permission ('readcopy' / 'readwrite' / etc) — already
   *  resolved through the App view-cap + dot-prefix rules. */
  permission: string
  /** fileId the link's grant cascades from. Used for "inherited from /AAA/"
   *  display when this row is shown on a descendant. */
  grantRootId: string
  accessMode: 'anonymous' | 'email_verify'
  allowedEmails: string[] | null
  expiresAt: string | null
  createdAt: string
}

/**
 * An EXTERNAL reshare link on a `.shared` that targets the owner's content.
 * Surfaced to the source owner (in Who-has-access → Links) so they can see and
 * revoke reshares of their content — parity with the drive-reshare path. Carries
 * the FULL link shape (so it renders the same card as an owner's own link) plus
 * `reshareFrom` attribution.
 */
export interface InboundReshareLink extends ShareLinkWithUrl {
  reshareFrom: {
    tenantName: string
    isOrg: boolean
    name: string | null
    email: string | null
    targetFileName: string | null
  }
}

export async function fetchShareLinkPrincipals(fileId: string): Promise<ShareLinkPrincipal[]> {
  const res = await request<{ principals: ShareLinkPrincipal[]; inboundReshares?: InboundReshareLink[] }>(
    `/api/share-links/principals?fileId=${encodeURIComponent(fileId)}`,
  )
  return res.principals
}

/** External reshare links on this file/folder (owner/admin only). Same source as
 *  the principals call; surfaced in both Who-has-access → Links and the Share
 *  links dialog. Returns [] for non-owner callers (server gates on admin+). */
export async function fetchInboundReshares(fileId: string): Promise<InboundReshareLink[]> {
  const res = await request<{ inboundReshares?: InboundReshareLink[] }>(
    `/api/share-links/principals?fileId=${encodeURIComponent(fileId)}`,
  )
  return res.inboundReshares ?? []
}

/** Source-owner revoke of an EXTERNAL reshare link on their content. Authorized
 *  server-side by admin+ on the source folder (cross-tenant). */
export async function ownerRevokeShareLink(id: string): Promise<void> {
  await request(`/api/share-links/${encodeURIComponent(id)}/owner-revoke`, { method: 'POST' })
}

/**
 * Ensure a DEFAULT NATIVE LINK (is_default) of the given kind exists — idempotent
 * find-or-create. Called when the Share dialog opens. `kind` defaults to
 * 'quick_view' (the universal source default, for any file/folder); pass 'app'
 * for the native App default of an HTML file / `.miniapp` folder. Returns the
 * default link. Callers usually just `.catch(() => {})` and then re-list.
 */
export async function ensureDefaultLink(fileId: string, kind: ShareKind = 'quick_view', subSourceFileId?: string): Promise<ShareLinkWithUrl> {
  return request<ShareLinkWithUrl>('/api/share-links/default', {
    method: 'POST',
    body: JSON.stringify({ fileId, kind, ...(subSourceFileId ? { subSourceFileId } : {}) }),
  })
}

/**
 * The App default for a loose HTML file whose folder is one of the drive's own
 * (the drive root, `Library`, a person's space…) is REFUSED (#888/#889): such a
 * file has no valid app folder. `ensureDefaultLink` throws on it like any failure;
 * this variant hands the refusal back so the dialog can explain it in place and
 * offer the move — `folder` is that structural folder (the file's parent).
 */
export type DefaultAppLinkResult =
  | { ok: true; link: ShareLinkWithUrl }
  | { ok: false; structural: true; reason: string; folder: { id: string; name: string }; detail: string }
  | { ok: false; structural: false; error: string }

export async function ensureDefaultAppLink(fileId: string, subSourceFileId?: string): Promise<DefaultAppLinkResult> {
  const res = await fetchRaw('/api/share-links/default', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileId, kind: 'app', ...(subSourceFileId ? { subSourceFileId } : {}) }),
  })
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (res.ok) return { ok: true, link: body as unknown as ShareLinkWithUrl }
  if (body.error === 'structural_app_root' && body.folder && typeof body.folder === 'object') {
    const folder = body.folder as { id?: unknown; name?: unknown }
    return {
      ok: false,
      structural: true,
      reason: String(body.reason ?? ''),
      folder: { id: String(folder.id ?? ''), name: String(folder.name ?? '') },
      detail: typeof body.detail === 'string' ? body.detail : '',
    }
  }
  return { ok: false, structural: false, error: typeof body.error === 'string' ? body.error : 'request_failed' }
}

/**
 * "Move into its own folder" (#888/#889): create a folder named after the HTML file
 * beside it and move the file in, so it has an app folder of its own. Only the HTML
 * moves — the files it uses stay where they are. A move the drive would only make
 * after a confirmation (it changes who can see the file) is NOT made here; the
 * caller says to move it from the Drive instead.
 */
export async function moveIntoOwnFolder(fileId: string, parentId: string, folderName: string): Promise<{ folderId: string; folderName: string }> {
  const { folder } = await request<{ folder: { id: string; name: string } }>('/api/files/folder', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ parentId, name: folderName, keepBoth: true }),
  })
  const res = await fetchRaw(`/api/files/${encodeURIComponent(fileId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ parentId: folder.id }),
  })
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (!res.ok || body.needsConfirmation || body.accessChange || body.success === false) {
    // Leave nothing half-done: the new folder is empty, so remove it again.
    await request(`/api/files/${encodeURIComponent(folder.id)}`, { method: 'DELETE' }).catch(() => {})
    throw new Error(
      body.accessChange || body.needsConfirmation
        ? 'Moving it would change who can see it. Move it from the Drive, where you can review that.'
        : typeof body.error === 'string' ? body.error : 'Couldn\'t move the file.',
    )
  }
  return { folderId: folder.id, folderName: folder.name }
}

/** List share-links for the active tenant, optionally filtered. */
export async function listShareLinks(args: ListShareLinksArgs = {}): Promise<ShareLinkWithUrl[]> {
  const params = new URLSearchParams()
  if (args.fileId) params.set('fileId', args.fileId)
  if (args.kind) params.set('kind', args.kind)
  if (args.includeRevoked) params.set('includeRevoked', '1')
  const qs = params.toString()
  const res = await request<{ shareLinks: ShareLinkWithUrl[] }>(
    `/api/share-links${qs ? `?${qs}` : ''}`,
  )
  return res.shareLinks
}

/** Manageable-link row — extends ShareLinkWithUrl with the
 *  root-file's name + parent_id so the Links settings page can group
 *  by rootFileId and label the left-panel entries without a second
 *  fetch. */
export interface ManageableShareLink extends ShareLinkWithUrl {
  rootFile: {
    id: string
    name: string
    parentId: string | null
    /** True when the source is a folder. Sourced from `files.is_folder`
     *  so the client doesn't have to guess from the file name. */
    isFolder: boolean
    /** Slash-separated path from the tenant root down to the file —
     *  used for hover tooltips and VS-Code-style ambiguous-name
     *  disambiguation in the settings sidebar. */
    path: string
  } | null
  /** The source still exists but this caller can't be shown where (#787) —
   *  `rootFile` is null for a different reason than a deleted source. */
  rootWithheld?: boolean
  /** Set when this row is an EXTERNAL reshare of this tenant's content (the link
   *  lives in the resharer's tenant). Drives the "External reshare" badge + the
   *  share glyph on the resharer avatar; absent for the tenant's own links. */
  reshareFrom?: {
    tenantName: string
    isOrg: boolean
    name: string | null
    email: string | null
    targetFileName: string | null
  }
}

/** Tenant-wide list of share-links the caller can manage (creator OR
 *  folder admin OR tenant admin/owner). Powers the Settings → Links
 *  page. Includes revoked rows; the UI filters them via a toolbar
 *  chip. */
export async function listManageableShareLinks(): Promise<ManageableShareLink[]> {
  const res = await request<{ shareLinks: ManageableShareLink[] }>(
    `/api/share-links/managed`,
  )
  return res.shareLinks
}

export interface UpdateShareLinkPatch {
  /** ISO string to set, null to clear. Undefined leaves unchanged. */
  expiresAt?: string | null
  /** (0084) Change the App root folder for a kind='app' share. Server
   *  validates ancestry + caller's read permission; bumps the share's
   *  config_version so existing sessions are force-re-minted. */
  appRootFileId?: string
  /** (0108) Per-permission Add-to-Drive approval bypass toggles. */
  autoApproveView?: boolean
  autoApproveEdit?: boolean
  /** (0119) Hard off-switch for recipient-initiated Add-to-Drive. */
  forbidAddToDrive?: boolean
}

/**
 * Update an owned share-link's LINK-LEVEL settings: `expiresAt`, plus
 * `appRootFileId` for App-kind shares (mutable under 0084). Per-
 * recipient grants live in `recipient_grants` and mutate via the
 * /recipients endpoints.
 */
export async function updateShareLink(
  id: string,
  patch: UpdateShareLinkPatch,
): Promise<ShareLinkWithUrl> {
  const res = await request<{ shareLink: ShareLinkWithUrl }>(
    `/api/share-links/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  )
  return res.shareLink
}

// ─── Decision 10 / Phase 34: Recipient sub-resource helpers ──────────
//
// Note: the bulk-replace `replaceRecipients` helper (PUT /:id/recipients)
// was removed in round-11 after `ShareLinksDialog` migrated to the
// granular PATCH endpoints below — bulk replace was last-writer-wins
// across concurrent tabs (round-9 #8). The server endpoint stays
// available for future programmatic callers that genuinely need
// atomic bulk replacement; this client just doesn't surface it
// anymore, removing a drift trap.

/**
 * Add or update a single recipient entry. `email` can be `"*"` for the
 * "Anyone with the link" wildcard. Server lowercases other emails. The
 * grant carries permission + liveMode together so the caller controls
 * per-recipient update visibility.
 */
export async function upsertRecipient(
  id: string,
  email: string,
  grant: RecipientGrant,
): Promise<ShareLinkWithUrl> {
  const res = await request<{ shareLink: ShareLinkWithUrl }>(
    `/api/share-links/${encodeURIComponent(id)}/recipients`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        upsert: { email, permission: grant.permission, liveMode: grant.liveMode },
      }),
    },
  )
  return res.shareLink
}

/** Remove a single recipient entry. Idempotent. */
export async function removeRecipient(
  id: string,
  email: string,
): Promise<ShareLinkWithUrl> {
  const res = await request<{ shareLink: ShareLinkWithUrl }>(
    `/api/share-links/${encodeURIComponent(id)}/recipients`,
    {
      method: 'PATCH',
      body: JSON.stringify({ remove: { email } }),
    },
  )
  return res.shareLink
}

// ─── 0085: Coverage extension ────────────────────────────────────────

export interface ExtendPreview {
  wouldExtend: boolean
  newlyCoveredEntityCount: number
  /** Present when wouldExtend is false. Lets the UI distinguish "no
   *  gap" from "you can't extend even if there is one." */
  reason?: 'not_authorized' | 'no_share_capability' | 'no_file_access' | 'walk_failed' | 'not_extendable'
}

/**
 * Ask whether the current caller could extend the share's coverage by
 * adding their access. Cheap enough to call on EditPanel open.
 */
export async function previewShareExtend(id: string): Promise<ExtendPreview> {
  return request<ExtendPreview>(
    `/api/share-links/${encodeURIComponent(id)}/extend-preview`,
  )
}

/** One thing an extension would newly make visible (or editable) on the link. */
export interface ExtendPreviewItem {
  id: string
  name: string
  /** `shortcut` = a `.chain` the extension would open (placed after the link was made). */
  kind: 'file' | 'folder' | 'shortcut'
  level: 'view' | 'edit'
  /** A folder: how many items inside it also become visible. A shortcut: how many items
   *  behind it the link would reach. */
  inside?: number
  targetName?: string
}

/** The Extend access card's Preview: WHAT the caller's access would add to the link. */
export async function previewShareExtendItems(id: string): Promise<ExtendPreview & { items?: ExtendPreviewItem[]; itemsTruncated?: boolean }> {
  return request(`/api/share-links/${encodeURIComponent(id)}/extend-preview?items=1`)
}

/**
 * Contribute the caller's access to the link's reach: the server appends the
 * caller's grant snapshot (0227), so per item recipients get the better of
 * what the link gave and what the caller can see, capped by the link's own
 * permission. `shareLink` (token + url) comes back ONLY when the caller has
 * authority over the link (#787 D3 / I5).
 */
export async function extendShareAccess(id: string): Promise<{
  shareLink?: ShareLinkWithUrl
  newlyCoveredEntityCount: number
}> {
  return request(
    `/api/share-links/${encodeURIComponent(id)}/extend-access`,
    { method: 'POST' },
  )
}

export interface ShareCapabilities {
  /** Caller has allow_sharing in the active tenant. */
  canShare: boolean
  /** Caller's FilePermission on the target file is >= admin. */
  canGrantEdit: boolean
}

/**
 * What the caller can do with a specific file: gate the share dialog
 * (UI mirrors the server-side mint guards in /app and /quick-view).
 */
export async function fetchShareCapabilities(fileId: string, opts: { subSourceFileId?: string } = {}): Promise<ShareCapabilities> {
  // A sub-item of a mount: the pointer + the sub-item, the same pair the mint receives.
  const sub = opts.subSourceFileId ? `&subSourceFileId=${encodeURIComponent(opts.subSourceFileId)}` : ''
  return request<ShareCapabilities>(
    `/api/share-links/capabilities?fileId=${encodeURIComponent(fileId)}${sub}`,
  )
}

/** Revoke an owned share-link. Server returns 410 on future serves. */
export async function revokeShareLink(id: string, opts?: { viaFolderId?: string }): Promise<void> {
  // `viaFolderId`: revoking from the Links tab of a folder the link reaches THROUGH A
  // SHORTCUT — the server judges Manage there.
  const qs = opts?.viaFolderId ? `?viaFolderId=${encodeURIComponent(opts.viaFolderId)}` : ''
  await request<{ ok: true; id: string }>(
    `/api/share-links/${encodeURIComponent(id)}${qs}`,
    { method: 'DELETE' },
  )
}

/** A link that reaches a folder THROUGH A SHORTCUT, as that folder's Links tab lists it —
 *  never its token / URL / root, nor a shortcut's path. */
export interface ViaShortcutLink {
  id: string
  kind: string
  createdAt: string
  creator: { name: string | null; email: string } | null
  via: 'shortcut'
  /** The viewer manages where the link enters (the shortcut's target) — may revoke it here. */
  canRevoke: boolean
  /** The shortcut(s) the link comes in through, by name — only those the viewer can open in
   *  their own drive; empty when they can open none (the row then says "via a shortcut"). */
  shortcuts?: Array<{ id: string; name: string }>
}

/**
 * Phase 1 App root picker: accessible ancestor chain for an HTML file.
 *
 * Reuses the script-runner endpoint (`/api/scripts/:fileId/run-from`)
 * — same data shape (file's parent → highest accessible ancestor,
 * clipped at the caller's access ceiling), same permission gate (the
 * caller must have access to the file). For the share dialog, this is
 * what we need to populate the Root folder picker without piping a
 * second endpoint through the API. Renamed locally to avoid leaking
 * the script-runner terminology into the share UI.
 */
export interface AppRootOption {
  folderId: string
  /** Folder name for the dropdown label. */
  name: string
  /** VFS-relative path. */
  relPath: string
  /** False for the drive's own folders (#889) — never an app link's folder. */
  shareable?: boolean
}

export interface AppRootOptions {
  /** File's parent → highest accessible ancestor. */
  options: AppRootOption[]
  /** Pre-selected folderId (the file's own parent when accessible). */
  defaultFolderId: string | null
  /** The share picker's default: the file's own folder unless it is structural (#889) — then none. */
  shareDefaultFolderId?: string | null
}

export async function fetchAppRootOptions(fileId: string): Promise<AppRootOptions> {
  return await request<AppRootOptions>(`/api/scripts/${encodeURIComponent(fileId)}/run-from`)
}

export type SendMagicEmailResponse =
  | { ok: true; sentAt: string; /** DEV ONLY: the one-click magic URL, so the
       *  dialog can show a testable link (no email reaches a real inbox in dev).
       *  Absent in production. */ devMagicUrl?: string }
  | { ok: false; kind: 'rate-limited'; retryAfterMs: number; lastSentAt: string | null }
  | { ok: false; kind: 'failed'; error: string }

/**
 * Phase 17 (Decision 8c): per-recipient magic-link send. Rate-limited
 * to 1/hour per (link, recipient); set `force=true` to override.
 *
 * Uses a raw fetch (not the api-client `request` helper) because the
 * 429 rate-limit response carries `retryAfterMs` + `lastSentAt` in its
 * body that the UI needs to render the "Sent <Nm> ago" badge. The
 * helper's error path stringifies the body to a message and loses the
 * structured fields.
 */
export async function sendShareLinkMagic(
  id: string,
  email: string,
  opts: { force?: boolean } = {},
): Promise<SendMagicEmailResponse> {
  const res = await fetchRaw(`/api/share-links/${encodeURIComponent(id)}/send-magic`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, force: opts.force ?? false }),
  })
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (res.status === 200 && body.ok === true) {
    return {
      ok: true,
      sentAt: (body.sentAt as string) ?? new Date().toISOString(),
      ...(typeof body.devMagicUrl === 'string' ? { devMagicUrl: body.devMagicUrl } : {}),
    }
  }
  if (res.status === 429 && body.error === 'rate_limited') {
    return {
      ok: false,
      kind: 'rate-limited',
      retryAfterMs: (body.retryAfterMs as number) ?? 0,
      lastSentAt: (body.lastSentAt as string | null) ?? null,
    }
  }
  return { ok: false, kind: 'failed', error: typeof body.error === 'string' ? body.error : 'send_failed' }
}
